import * as React from 'react';
import { Link } from 'react-router-dom';
import { withRouter, RouteComponentProps } from 'react-router';

import styled, { tstyled, keyframes } from '../../theme';
import ProjectType from '../../models/ProjectType';

interface IProps {
  projectType: string;
  typeProjectsMap: typeof ProjectType.typeProjectsMap;
  archived: boolean;
}

interface IState {
  fixed: boolean;
}

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const breakpoint = 1360;

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 30px;
  min-width: 160px;
  display: none;
  width: 11%;

  > * {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (min-width: ${breakpoint}px) {
    display: block;
  }
`;

interface ILink {
  active?: boolean;
}

const padding = 50;

const LinkContainer = tstyled<ILink, HTMLDivElement>(styled.div)`
  a {
    color: ${(props) =>
      props.active ? props.theme.color.body2 : props.theme.color.body1};
    text-decoration: none;
  }

  animation-name: ${fadeIn};
  animation-fill-mode: forwards;
  animation-duration: 0.8s;
  animation-delay: 0.5s;
  opacity: 0;

  font-size: 12px;
  line-height: 1.6;

  transition: 0.2s ease-in-out transform;
  will-change: transform;

  &:hover {
    transform: translateX(10px);
  }


  @media (min-width: 1800px) {
    font-size: 0.6vw;
  }
`;

function documentScrollTop() {
  return document.documentElement.scrollTop || document.body.scrollTop || 0;
}

class Nav extends React.Component<
  IProps &
    RouteComponentProps<{
      projectSlug: string;
      projectType: string;
    }>,
  IState
> {
  state = {
    fixed: false,
  };

  private boundHandler: boolean = false;

  container: HTMLDivElement;
  offsetTop: number;

  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
  }

  onResize = () => {
    if (window.innerWidth < breakpoint) {
      if (this.boundHandler) {
        this.boundHandler = false;
        window.removeEventListener('scroll', this.onScroll);
      }
    } else {
      if (!this.boundHandler) {
        this.boundHandler = true;
        setTimeout(() => {
          this.offsetTop =
            this.container.getBoundingClientRect().top + window.pageYOffset;
        }, 0);
        window.addEventListener('scroll', this.onScroll);
      }
    }
  };

  calculateNextFixed = () => {
    let offsetTop: number;

    if (this.state.fixed) {
      offsetTop = this.offsetTop;
    } else {
      offsetTop =
        this.container.getBoundingClientRect().top + window.pageYOffset;
    }
    const shouldBeFixed = documentScrollTop() + padding >= offsetTop;

    if (this.state.fixed !== shouldBeFixed) {
      if (!this.state.fixed) {
        this.offsetTop = offsetTop;
      }
      this.setState({ fixed: shouldBeFixed });
    }
  };

  onScroll = () => {
    requestAnimationFrame(this.calculateNextFixed);
  };

  onClickLink = (slug: string) => () => {
    this.props.history.replace(
      `${this.props.archived ? '/archive' : ''}/project/${
        this.props.projectType
      }/${slug}`
    );
  };

  render() {
    const allProjects = this.props.typeProjectsMap.get(this.props.projectType);
    if (undefined === allProjects) {
      return null;
    }
    const projects = this.props.archived
      ? allProjects.filter((project) => !!project.fields.archived)
      : allProjects.filter((project) => !project.fields.archived);

    const style: Record<string, any> = {};
    if (this.state.fixed) {
      style.position = 'fixed';
      style.top = 50;
    }

    return (
      <Container style={style} innerRef={(ref) => (this.container = ref)}>
        {projects.map((project, index) => {
          if (!project.fields) {
            return null;
          }

          const slug = project.fields.slug;
          return (
            <LinkContainer
              key={slug}
              active={this.props.match.params.projectSlug === slug}
              onClick={this.onClickLink(slug)}
              style={{
                animationDelay: `${1.2 + 0.1 * index}s`,
              }}
            >
              <Link
                to={`${this.props.archived ? '/archive' : ''}/project/${
                  this.props.projectType
                }/${slug}`}
              >
                {project.fields.name}
              </Link>
            </LinkContainer>
          );
        })}
      </Container>
    );
  }
}

export default withRouter(Nav);



// WEBPACK FOOTER //
// ./src/scenes/Project/Nav.tsx